import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { X } from "@phosphor-icons/react";
import { useFocusTrap } from "@/lib/use-focus-trap";
import { cn } from "@/lib/cn";
import { IconButton } from "./IconButton";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  side?: "right" | "left" | "bottom";
  className?: string;
}

/** Slide-in panel: focus trap, Esc to close, scrim click to close. DESIGN.md §9/§7. */
export function Drawer({ open, onClose, title, children, side = "right", className }: DrawerProps) {
  const ref = useFocusTrap<HTMLDivElement>(open, onClose);
  if (!open) return null;

  return createPortal(
    <div className={cn("fixed inset-0 z-50 flex", side === "bottom" ? "items-end" : undefined)}>
      <div className="absolute inset-0 bg-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        className={cn(
          "relative z-10 flex w-full flex-col border-border bg-surface-1 p-6 shadow-[var(--shadow-popover)]",
          side === "bottom" ? "max-h-[min(32rem,calc(100dvh-4rem))] rounded-t-lg border-t" : "h-full max-w-sm",
          side === "right" ? "ml-auto border-l" : side === "left" ? "mr-auto border-r" : undefined,
          className,
        )}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id="drawer-title" className="text-h4">
            {title}
          </h2>
          <IconButton label="Close" icon={<X size={18} weight="regular" />} onClick={onClose} />
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
